import { Typography } from '@healingpaper/cell'
import { differenceInCalendarDays } from 'date-fns'
import { memo, useEffect, useRef } from 'react'
import useImpressionTracker from 'react-impression-tracker'
import styled from 'styled-components'
import { IcCheckMarkFilled } from '@healingpaper/cell-icons'
import { useTranslation } from 'react-i18next'

import { theme } from '@/shared/design-system/theme'
import CheetahIcon, { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon'
import Text from '@/components/atomic/atoms/Text'
import { getPriceWithMonetaryUnit } from '@/utils/price'
import { generateTagId } from '@/utils/test-automation'
import { Countries, type Locales } from '@/utils/language'
import { getResizedImageUrl, ResizableImageFormats } from '@/utils/image-resizer/imageResizer'
import { Currency, ServiceOffer } from '@/features/service-offer/service-offer.type'

export interface EventListProps {
  serviceOffer: ServiceOffer
  text: {
    vat: string
    certifiedHospital: string
    specialPrice: string
  }
  urlCountry: Countries // 한국향, 일본향을 나타냄
  isImpressionVisible?: boolean
  divider?: boolean
  onClick: (event: EventListItemOnClickEventParam) => void
  onImpressed?: () => void
  hospitalSiGunGu?: string
  showVatBadge?: boolean
  index?: number
  // Legacy Event 속성들 (serviceOffer에 없는 데이터)
  assessmentState?: string
  hospitalSubway?: string
  createTime?: Date | number
  useBuyNow?: boolean
}

export interface EventListItemOnClickEventParam {
  id: number
  title: string
  hospitalId: number
  discountedCost: number
  cost: number
  currency: string
  discountRate: number
  hospitalCountry?: string
  hospitalSido?: string
}

const getThumbnailUrl = (thumbnail: string | { url: string }): string => {
  return typeof thumbnail === 'string' ? thumbnail : thumbnail.url
}

const EventListItem = (props: EventListProps): JSX.Element => {
  const {
    serviceOffer,
    urlCountry,
    text,
    onClick,
    onImpressed,
    divider = true,
    isImpressionVisible = false,
    hospitalSiGunGu,
    showVatBadge,
    index,
    // Legacy props (serviceOffer에 없는 데이터)
    assessmentState,
    hospitalSubway,
    createTime,
    useBuyNow,
  } = props

  // serviceOffer에서 데이터 추출
  const id = serviceOffer.id
  const titleImage = getThumbnailUrl(serviceOffer.thumbnail)
  const title = serviceOffer.title
  const hospitalId = serviceOffer.hospital.id
  const hospitalName = serviceOffer.hospital.name
  const hospitalCountry = serviceOffer.hospital.country
  const hospitalSido = '' // serviceOffer에 없는 데이터
  const rating = serviceOffer.rating.totalRating
  const ratingCount = serviceOffer.rating.ratingCount
  const discountedCost = serviceOffer.price.discountedAmount
  const cost = serviceOffer.price.originalAmount
  const currency = serviceOffer.price.currency as Currency

  const { i18n } = useTranslation()
  const isStart = useRef(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const { targetRef, startTracking } = useImpressionTracker<HTMLDivElement, object>({
    context: {},
    onImpressed: () => onImpressed?.(),
    isImpressionVisible,
    hasImpressed: false,
    rootMargin: '-66px',
  })

  const isNew = !!createTime && differenceInCalendarDays(new Date(), createTime) < 90 && ratingCount < 10
  const discountRate = serviceOffer.price.discountPercentage

  const handleClick = () => {
    onClick({
      id,
      title,
      hospitalId,
      hospitalCountry: hospitalCountry ?? undefined,
      hospitalSido: hospitalSido ?? undefined,
      discountedCost,
      cost,
      currency,
      discountRate,
    })
  }

  useEffect(() => {
    if (imgRef.current?.complete) {
      isStart.current = true
      startTracking()
    }
  }, [startTracking])

  return (
    <StyledContainer
      id={generateTagId('event', 'card', 'component', 'ui', index?.toString() || id.toString())}
      aria-label="event list item"
      ref={targetRef}
      $divider={divider}
      onClick={handleClick}
    >
      <StyledImageWrapper>
        <StyledImage
          ref={imgRef}
          src={getResizedImageUrl(titleImage, { format: ResizableImageFormats.WEBP, width: 320, height: 320 })}
          alt={title}
          onLoad={() => {
            if (!isStart.current) {
              isStart.current = true
              startTracking()
            }
          }}
        />
      </StyledImageWrapper>

      <StyledContent>
        {assessmentState === 'CERTIFIED' && (
          <StyledCertifiedHospitalBadge>
            <IcCheckMarkFilled width={13} height={13} color={theme.colors.certified} />
            <StyledCertificatedText>{text.certifiedHospital}</StyledCertificatedText>
          </StyledCertifiedHospitalBadge>
        )}
        <StyledHospital>
          {(!!hospitalSubway || urlCountry === Countries.KR) && (
            <Typography type="caption2" color="gray800">
              {hospitalSubway && hospitalSubway}
              {!hospitalSubway && (hospitalSiGunGu ? hospitalSiGunGu : hospitalSido)}
            </Typography>
          )}
          <Typography type="caption2" color="gray800">
            {hospitalName}
          </Typography>
        </StyledHospital>
        <StyledTitle type="description1" element="h3" numberOfLines={1}>
          {title}
        </StyledTitle>
        {(isNew || (!isNew && ratingCount > 1)) && (
          <StyledBadge>
            <StyledStar name={CheetahIconPack.STAR} width={12} height={12} />
            {isNew && (
              <Text type="caption1" color={theme.colors.gray700}>
                NEW
              </Text>
            )}
            {!isNew && ratingCount > 1 && (
              <>
                <StyledRating type="caption1">{rating}</StyledRating>
                <Text type="caption1" color={theme.colors.gray700}>
                  {`(${ratingCount})`}
                </Text>
              </>
            )}
          </StyledBadge>
        )}
        <StyledPriceInfo>
          <StyledPrice aria-label="price">
            {discountedCost !== 0
              ? getPriceWithMonetaryUnit({
                  currency,
                  locale: i18n.language as Locales,
                  price: discountedCost,
                })
              : ''}
          </StyledPrice>
          {serviceOffer.price.specialOffer ? (
            <StyledDiscount type="description1" color={theme.colors.latte}>
              {text.specialPrice}
            </StyledDiscount>
          ) : (
            discountRate > 0 && (
              <StyledDiscount type="description1" color={theme.colors.latte}>
                {discountRate}%
              </StyledDiscount>
            )
          )}
          {showVatBadge && <StyledVat>{text.vat}</StyledVat>}
        </StyledPriceInfo>

        {urlCountry === Countries.KR && useBuyNow && <StyledBuyNowBadge>앱에서결제</StyledBuyNowBadge>}
      </StyledContent>
    </StyledContainer>
  )
}

const StyledContainer = styled.div<{ $divider: boolean }>`
  display: flex;
  padding: 16px 26px;
  cursor: pointer;
  ${({ $divider }) => $divider && 'border-bottom: 1px solid #ececec;'}
`
const StyledImageWrapper = styled.div`
  flex-shrink: 0;
  margin-right: 16px;
  font-size: 0px;
`
const StyledImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 4px;
`
const StyledContent = styled.div``
const StyledHospital = styled.div`
  color: ${({ theme }) => theme.colors.gray800};
  & > * + * {
    &::before {
      content: '·';
      margin-left: 2px;
      margin-right: 2px;
    }
  }
`
const StyledTitle = styled(Text)`
  margin-bottom: 10px;
`
const StyledBadge = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`
const StyledStar = styled(CheetahIcon)`
  margin-right: 4px;
`
const StyledRating = styled(Text)`
  margin-right: 2px;
`
const StyledPriceInfo = styled.div`
  display: flex;
`
const StyledPrice = styled.div`
  ${({ theme }) => theme.typography.title3}
  margin-right: 4px;
`
const StyledDiscount = styled(Text)`
  margin-right: 4px;
`
const StyledVat = styled.div`
  align-self: flex-end;
  color: #999999;
  font-size: 10px;
  line-height: 12px;
`
const StyledCertifiedHospitalBadge = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`
const StyledCertificatedText = styled.span`
  font-weight: 700;
  font-size: 12px;
  color: #4c6ef5;
`
const StyledBuyNowBadge = styled.span`
  display: inline-block;
  padding: 4px 6px;
  color: #0e84a3;
  background-color: #eafafe;
  border-radius: 2px;
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  margin-top: 4px;
`

export default memo(EventListItem)
